import { RisError } from '../client';

/**
 * A transcription request that cannot hang and does not give up on the first hiccup: each attempt
 * has a timeout, and a network error, a timeout or a 5xx is tried again after a short wait (one
 * timer per wait, never a loop over an endpoint). A 4xx (rate limit, no permission, bad audio) is
 * answered at once: asking again would get the same answer.
 */

/** One attempt may take this long: ris answers a 60 s clip well within it. */
export const TRANSCRIBE_TIMEOUT_MS = 90_000;
/** Attempts in all (the first one plus retries). */
export const TRANSCRIBE_ATTEMPTS = 3;
/** Wait before the n-th retry: 1 s, then 3 s. */
export const TRANSCRIBE_BACKOFF_MS = [1_000, 3_000] as const;

const FIRST_SERVER_ERROR = 500;

export interface RetryOptions {
  attempts?: number;
  timeoutMs?: number;
  backoffMs?: readonly number[];
  /** Injected in the unit spec so it does not wait for real. */
  sleep?: (ms: number) => Promise<void>;
}

/** The request timed out (ours, not the server's 504). */
export class TranscribeTimeout extends Error {
  constructor() {
    super('transcription timed out');
  }
}

/** True for a failure worth another attempt: no network, our timeout, or the server's 5xx. */
export function isRetryable(error: unknown): boolean {
  if (error instanceof RisError) return error.status >= FIRST_SERVER_ERROR;
  if (error instanceof TranscribeTimeout) return true;
  // fetch rejects with a TypeError when the network is down or the connection drops.
  return error instanceof TypeError;
}

const realSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

async function attempt<T>(run: (signal: AbortSignal) => Promise<T>, timeoutMs: number): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    return await run(controller.signal);
  } catch (error) {
    throw timedOut ? new TranscribeTimeout() : error;
  } finally {
    clearTimeout(timer);
  }
}

/** Runs `run` with a timeout per attempt, retrying a retryable failure; throws the last error. */
export async function withRetry<T>(run: (signal: AbortSignal) => Promise<T>, options: RetryOptions = {}): Promise<T> {
  const attempts = Math.max(1, options.attempts ?? TRANSCRIBE_ATTEMPTS);
  const backoff = options.backoffMs ?? TRANSCRIBE_BACKOFF_MS;
  const sleep = options.sleep ?? realSleep;
  let lastError: unknown;
  for (let n = 0; n < attempts; n += 1) {
    if (n > 0) await sleep(backoff[Math.min(n - 1, backoff.length - 1)] ?? 0);
    try {
      return await attempt(run, options.timeoutMs ?? TRANSCRIBE_TIMEOUT_MS);
    } catch (error) {
      lastError = error;
      if (!isRetryable(error)) break;
    }
  }
  throw lastError;
}
